import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "@/packages/utils";
import { AuthLayout } from "./AuthLayout";
import { ForgotPasswordForm } from "./ForgotPasswordForm";
import { OtpVerificationForm } from "./OtpVerificationForm";
import { ResetPasswordForm } from "./ResetPasswordForm";
import { SignInForm } from "./SignInForm";
import { SignUpForm } from "./SignUpForm";

type AuthFlow =
  | "signIn"
  | "signUp"
  | "otp-verification"
  | "forgot-password"
  | "reset-otp"
  | "reset-password";

export interface AuthPageProps {
  flow?: AuthFlow;
  onFlowChange?: (flow: AuthFlow) => void;
  brandName?: string;
  onSignInSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  signInIsLoading?: boolean;
  signInError?: string | null;
  onSignUpSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  signUpPassword: string;
  onSignUpPasswordChange: (value: string) => void;
  signUpIsLoading?: boolean;
  signUpError?: string | null;
  signUpHasAttemptedSubmit?: boolean;
  // OTP verification props
  onOtpSubmit?: (code: string) => void;
  onOtpResend?: () => void;
  onOtpBack?: () => void;
  otpIsLoading?: boolean;
  otpError?: string | null;
  verificationEmail?: string | null;
  initialOtpCode?: string;
  // Forgot password
  onForgotPasswordSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  forgotPasswordIsLoading?: boolean;
  forgotPasswordError?: string | null;
  // Reset OTP
  onResetOtpSubmit?: (code: string) => void;
  onResetOtpResend?: () => void;
  onResetOtpBack?: () => void;
  resetOtpIsLoading?: boolean;
  resetOtpError?: string | null;
  resetVerificationEmail?: string | null;
  // Reset password
  onResetPasswordSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  onResetPasswordBack?: () => void;
  resetPasswordIsLoading?: boolean;
  resetPasswordError?: string | null;
  // Trigger forgot-password from sign-in
  onForgotPassword?: () => void;
}

const formTransition = {
  enter: { opacity: 0, x: 10 },
  center: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] as const },
  },
  exit: {
    opacity: 0,
    x: -10,
    transition: { duration: 0.18 },
  },
};

export function AuthPage({
  flow = "signIn",
  onFlowChange,
  onSignInSubmit,
  signInIsLoading,
  signInError,
  onSignUpSubmit,
  signUpPassword,
  onSignUpPasswordChange,
  signUpIsLoading,
  signUpError,
  signUpHasAttemptedSubmit,
  onOtpSubmit,
  onOtpResend,
  onOtpBack,
  otpIsLoading,
  otpError,
  verificationEmail,
  initialOtpCode,
  onForgotPasswordSubmit,
  forgotPasswordIsLoading,
  forgotPasswordError,
  onResetOtpSubmit,
  onResetOtpResend,
  onResetOtpBack,
  resetOtpIsLoading,
  resetOtpError,
  resetVerificationEmail,
  onResetPasswordSubmit,
  onResetPasswordBack,
  resetPasswordIsLoading,
  resetPasswordError,
  onForgotPassword,
}: AuthPageProps) {
  const { t } = useTranslation(["auth"]);
  const [internalFlow, setInternalFlow] = useState<AuthFlow>(flow);
  const currentFlow = onFlowChange ? flow : internalFlow;

  const handleFlowToggle = () => {
    const newFlow = currentFlow === "signIn" ? "signUp" : "signIn";
    if (onFlowChange) {
      onFlowChange(newFlow);
    } else {
      setInternalFlow(newFlow);
    }
  };

  const { headline, subline } = (() => {
    switch (currentFlow) {
      case "signIn":
        return {
          headline: t("auth:panelContent.signIn.headline"),
          subline: t("auth:panelContent.signIn.subline"),
        };
      case "signUp":
        return {
          headline: t("auth:panelContent.signUp.headline"),
          subline: t("auth:panelContent.signUp.subline"),
        };
      case "otp-verification":
        return {
          headline: t("auth:panelContent.otpVerification.headline"),
          subline: t("auth:panelContent.otpVerification.subline"),
        };
      case "forgot-password":
        return {
          headline: t("auth:panelContent.forgotPassword.headline"),
          subline: t("auth:panelContent.forgotPassword.subline"),
        };
      case "reset-otp":
        return {
          headline: t("auth:panelContent.resetOtp.headline"),
          subline: t("auth:panelContent.resetOtp.subline"),
        };
      case "reset-password":
        return {
          headline: t("auth:panelContent.resetPassword.headline"),
          subline: t("auth:panelContent.resetPassword.subline"),
        };
    }
  })();

  return (
    <AuthLayout
      headline={headline as string}
      subline={subline as string}
      onBrandClick={() => {
        window.location.href = "https://www.audinero.de/";
      }}
    >
      <AnimatePresence mode="wait">
        {currentFlow === "otp-verification" &&
        onOtpSubmit &&
        onOtpResend &&
        onOtpBack &&
        verificationEmail ? (
          <motion.div
            key="otp"
            variants={formTransition}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <OtpVerificationForm
              email={verificationEmail}
              onSubmit={onOtpSubmit}
              onResend={onOtpResend}
              onBack={onOtpBack}
              isLoading={otpIsLoading}
              error={otpError}
              initialCode={initialOtpCode}
            />
          </motion.div>
        ) : currentFlow === "forgot-password" && onForgotPasswordSubmit ? (
          <motion.div
            key="forgot-password"
            variants={formTransition}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <ForgotPasswordForm
              onSubmit={onForgotPasswordSubmit}
              isLoading={forgotPasswordIsLoading}
              error={forgotPasswordError}
              onBack={handleFlowToggle}
            />
          </motion.div>
        ) : currentFlow === "reset-otp" &&
          onResetOtpSubmit &&
          onResetOtpResend &&
          onResetOtpBack &&
          resetVerificationEmail ? (
          <motion.div
            key="reset-otp"
            variants={formTransition}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <OtpVerificationForm
              email={resetVerificationEmail}
              onSubmit={onResetOtpSubmit}
              onResend={onResetOtpResend}
              onBack={onResetOtpBack}
              isLoading={resetOtpIsLoading}
              error={resetOtpError}
            />
          </motion.div>
        ) : currentFlow === "reset-password" && onResetPasswordSubmit ? (
          <motion.div
            key="reset-password"
            variants={formTransition}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <ResetPasswordForm
              onSubmit={onResetPasswordSubmit}
              isLoading={resetPasswordIsLoading}
              error={resetPasswordError}
              onBack={onResetPasswordBack ?? (() => {})}
              email={resetVerificationEmail}
            />
          </motion.div>
        ) : currentFlow === "signUp" ? (
          <motion.div
            key="signup"
            variants={formTransition}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <SignUpForm
              onSubmit={onSignUpSubmit}
              password={signUpPassword}
              onPasswordChange={onSignUpPasswordChange}
              isLoading={signUpIsLoading}
              error={signUpError}
              hasAttemptedSubmit={signUpHasAttemptedSubmit}
              onToggleFlow={handleFlowToggle}
            />
          </motion.div>
        ) : (
          <motion.div
            key="signin"
            variants={formTransition}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <SignInForm
              onSubmit={onSignInSubmit}
              isLoading={signInIsLoading}
              error={signInError}
              onToggleFlow={handleFlowToggle}
              onForgotPassword={onForgotPassword}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </AuthLayout>
  );
}
